/**
 * AnnotationOverlay — HTML + SVG overlay rendered outside the R3F Canvas.
 *
 * Receives pre-computed AnnotationRenderData from AnnotationSensor (inside
 * the Canvas) and uses computeAnnotationLayouts() to place the visual elements.
 *
 * Visual anatomy per the SVG design:
 *
 *   ● — 45° line → elbow → horizontal line (label width)
 *                                           ┌───────────────┐
 *                                           │  NAME (bold)  │
 *                                           │  description  │
 *                                           └───────────────┘
 *
 * The overlay is pointer-events: none so it doesn't interfere with orbit controls.
 * Annotations fade in/out via CSS opacity transition.
 */

import { useMemo, useRef, useEffect } from 'react';
import {
  computeAnnotationLayouts,
  type AnnotationRenderData,
  type AnnotationLayout,
} from './annotationLayout';

// ---------------------------------------------------------------------------
// Constants matching annotationLayout.ts
// ---------------------------------------------------------------------------

const DOT_RADIUS = 7.5;
const STROKE_WIDTH = 2;
const NAME_FONT_SIZE = 13;
const DESC_FONT_SIZE = 11;
const TEXT_LINE_GAP = 4;

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface AnnotationOverlayProps {
  renderData: AnnotationRenderData[];
  viewportWidth: number;
  viewportHeight: number;
}

// ---------------------------------------------------------------------------
// Single annotation rendered as SVG lines + HTML text
// ---------------------------------------------------------------------------

function AnnotationItem({ layout }: { layout: AnnotationLayout }) {
  const {
    dotX,
    dotY,
    elbowX,
    elbowY,
    lineEndX,
    textAnchor,
    name,
    description,
    corner,
  } = layout;

  // Text is anchored at lineEndX (the far end of the horizontal line, away
  // from the 45° leader). The anchor direction means text extends back TOWARD
  // the object over the horizontal line — acting as a label underline.
  const textX = lineEndX;

  // Vertical placement: for TL/TR the leader goes UP so text is ABOVE the line.
  // For BL/BR the leader goes DOWN so text is BELOW the line.
  // In SVG, y is the alphabetic baseline (dominantBaseline='auto').
  const isGoingUp = corner === 'TL' || corner === 'TR';

  // Going up: name baseline above line, description baseline just above the line
  // Going down: name baseline just below line, description baseline below name
  const nameY = isGoingUp
    ? elbowY -
      TEXT_LINE_GAP -
      (description ? DESC_FONT_SIZE + TEXT_LINE_GAP : 0)
    : elbowY + TEXT_LINE_GAP + NAME_FONT_SIZE;
  const descY = isGoingUp
    ? elbowY - TEXT_LINE_GAP
    : nameY + TEXT_LINE_GAP + DESC_FONT_SIZE;

  return (
    <g>
      {/* Dot */}
      <circle cx={dotX} cy={dotY} r={DOT_RADIUS} fill="white" />
      {/* 45° leader line */}
      <line
        x1={dotX}
        y1={dotY}
        x2={elbowX}
        y2={elbowY}
        stroke="white"
        strokeWidth={STROKE_WIDTH}
      />
      {/* Horizontal line */}
      <line
        x1={elbowX}
        y1={elbowY}
        x2={lineEndX}
        y2={elbowY}
        stroke="white"
        strokeWidth={STROKE_WIDTH}
      />
      {/* Name text */}
      <text
        x={textX}
        y={nameY}
        textAnchor={textAnchor}
        fill="white"
        fontSize={NAME_FONT_SIZE}
        fontWeight="600"
        fontFamily="system-ui, -apple-system, sans-serif"
        dominantBaseline="auto"
      >
        {name}
      </text>
      {/* Description text */}
      {description && (
        <text
          x={textX}
          y={descY}
          textAnchor={textAnchor}
          fill="rgba(255,255,255,0.7)"
          fontSize={DESC_FONT_SIZE}
          fontFamily="system-ui, -apple-system, sans-serif"
          dominantBaseline="auto"
        >
          {description}
        </text>
      )}
    </g>
  );
}

// ---------------------------------------------------------------------------
// Main overlay component
// ---------------------------------------------------------------------------

export function AnnotationOverlay({
  renderData,
  viewportWidth,
  viewportHeight,
}: AnnotationOverlayProps) {
  const layouts = useMemo(
    () => computeAnnotationLayouts(renderData, viewportWidth, viewportHeight),
    [renderData, viewportWidth, viewportHeight],
  );

  if (layouts.length === 0 || viewportWidth === 0 || viewportHeight === 0) {
    return null;
  }

  return (
    <div
      className="pointer-events-none absolute inset-0 overflow-hidden"
      aria-hidden="true"
    >
      <svg
        width={viewportWidth}
        height={viewportHeight}
        className="absolute inset-0"
        style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.6))' }}
      >
        {layouts.map((layout, i) => (
          <AnnotationItem key={`${layout.name}-${i}`} layout={layout} />
        ))}
      </svg>
    </div>
  );
}
